// Order Requisition Print — one slip per patient per department.
//
// The Tests & Examinations board is organised the way the bench works: by
// order, by sample, by status. A requisition is organised the way paper works:
// pick the patient, pick the department the slip is going to, and everything
// that patient has pending there prints together, however many separate orders
// the doctors raised it across.
//
// Department is the order's service classification (utils/labDepartments.js),
// off the live get_patient_lab_tests/ board like everything else here.
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import axios from "axios";
import SvgIcon from "../ipd-billing/SvgIcon";
import { C, getAuthHeaders } from "../ipd-billing/constants";
import { BrandingContext } from "../../BrandingProvider";
import { usePermissions } from "../login/PermissionContext";
import { cardStyle, inputStyle, PageHeader } from "../rule-builder/ui";
import LabLayout from "./LabLayout";
import {
  PriorityPill,
  computeAge,
  dedupeOrdersByOrderNo,
  fetchLabTestsPage,
  formatDateTime,
  mergePatientDemographics,
} from "./labOrdersShared";
import { departmentCounts, resolveLabDepartment } from "./utils/labDepartments";
import { printLabOrderRequisition } from "./utils/printLabOrder";

const ALL_DEPARTMENTS = "__all__";
const SEARCH_DEBOUNCE_MS = 350;
const LIST_PAGE_SIZE = 50;
// One patient never has this many open tests; it is only here so the follow-up
// fetch cannot silently truncate the "everything for this patient" promise.
const PATIENT_PAGE_SIZE = 100;

const dash = (value) => {
  const s = String(value ?? "").trim();
  return s && s !== "-" ? s : "";
};

/** "Room 101 · Bed GP101A", or the encounter type when there is no bed. */
const formatLocation = (patient) => {
  const parts = [
    dash(patient?.roomNo),
    dash(patient?.bedNo) && `Bed ${dash(patient.bedNo)}`,
  ].filter(Boolean);
  if (parts.length) return parts.join(" · ");
  return patient?.patientType === "IPD" ? "-" : dash(patient?.patientType) || "-";
};

const resolveAge = (patient) => {
  const age = patient?.age ?? computeAge(patient?.dob);
  return age == null || age === "" ? null : age;
};

const formatAgeGender = (patient) => {
  const age = resolveAge(patient);
  const gender = dash(patient?.gender);
  return (
    [age == null ? null : `${age} Y`, gender].filter(Boolean).join(" / ") || "-"
  );
};

/**
 * The board returns one row per lab_test for OPD and one per admission for
 * IPD. A requisition is per patient, so collapse on UHID and merge the orders.
 */
function groupPatients(rows) {
  const byPatient = new Map();
  (rows || []).forEach((row) => {
    const key = dash(row.uhid) || String(row.id);
    const existing = byPatient.get(key);
    if (!existing) {
      byPatient.set(key, { ...row, key, orders: [...(row.orders || [])] });
      return;
    }
    // Row order is the API's, not the patient's: the row that carries the
    // demographics is not necessarily the first one to mention this UHID.
    mergePatientDemographics(existing, row);
    const seen = new Set(existing.orders.map((o) => o.orderNo));
    (row.orders || []).forEach((o) => {
      if (seen.has(o.orderNo)) return;
      seen.add(o.orderNo);
      existing.orders.push(o);
    });
  });
  return Array.from(byPatient.values()).sort((a, b) =>
    String(a.name).localeCompare(String(b.name)),
  );
}

// The bulk list leaves the patient record off rows it has not joined it onto:
// gender/dob/phone come back "-" and age null. The values do arrive on a query
// keyed by the order itself, which is the refetch the Tests & Examinations
// board runs when a row is expanded — so ask for it, but only when the slip
// would otherwise print a row of dashes.
const DEMOGRAPHIC_FIELDS = ["age", "gender", "phone"];

const isMissingDemographic = (value) =>
  value === undefined || value === null || value === "" || value === "-";

// Only an admitted patient has a ward and bed, so a missing one is not a gap
// worth a second request for anybody else.
const needsDemographics = (patient) =>
  DEMOGRAPHIC_FIELDS.some((key) => isMissingDemographic(patient?.[key])) ||
  (patient?.patientType === "IPD" &&
    isMissingDemographic(patient?.bedNo) &&
    isMissingDemographic(patient?.roomNo));

// The same lean rows also arrive without the two columns the slip is organised
// by. An admitted patient is already fetched by admission_id, which answers
// with the full order lines; everyone else is found by search, which does not —
// so an ED order printed as "Unclassified · -" while the identical test on an
// admitted patient printed "Haematology · EDTA Whole Blood".
const needsOrderDetail = (patient) =>
  (patient?.orders || []).some(
    (o) => isMissingDemographic(o?.department) || isMissingDemographic(o?.sample),
  );

/**
 * The board's detail query for a patient the search route served, or null when
 * the row carries no key to ask by. An admitted patient never gets here: that
 * selection is already fetched by admission_id, which is the detail query.
 */
const detailQueryFor = (patient) => {
  if (patient?.labTestId) return { labTestId: patient.labTestId };
  if (patient?.appointmentId) {
    return {
      appointmentId: patient.appointmentId,
      patientType: patient.patientType === "EMERGENCY" ? "EMERGENCY" : "OPD",
    };
  }
  return null;
};

function PatientListRow({ patient, active, onSelect }) {
  const age = resolveAge(patient);
  const meta = [
    dash(patient.uhid),
    age == null ? null : `${age} Y`,
    formatLocation(patient),
  ]
    .filter((v) => v && v !== "-")
    .join(" · ");

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onSelect(patient)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect(patient);
        }
      }}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "11px 14px",
        cursor: "pointer",
        borderBottom: `1px solid ${C.border}`,
        borderLeft: `3px solid ${active ? C.primary : "transparent"}`,
        background: active ? "#EEF6FD" : "#fff",
      }}
    >
      <span
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: 30,
          height: 30,
          borderRadius: "50%",
          background: "#EEF2F6",
          flexShrink: 0,
        }}
      >
        <SvgIcon name="users" size={15} color={C.textSub} />
      </span>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div
          style={{
            fontSize: 13,
            fontWeight: 700,
            color: C.text,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {patient.name}
        </div>
        <div
          style={{
            fontSize: 11,
            color: C.textSub,
            marginTop: 2,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {meta || "-"}
        </div>
      </div>
      <span
        style={{
          flexShrink: 0,
          minWidth: 22,
          textAlign: "center",
          padding: "2px 7px",
          borderRadius: 8,
          fontSize: 11,
          fontWeight: 700,
          background: active ? C.primary : "#EEF2F6",
          color: active ? "#fff" : C.textSub,
        }}
      >
        {patient.orders.length}
      </span>
    </div>
  );
}

function InfoField({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: C.textSub, marginBottom: 3 }}>
        {label}
      </div>
      <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>
        {value || "-"}
      </div>
    </div>
  );
}

function DepartmentChip({ label, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: "6px 14px",
        borderRadius: 20,
        border: `1.5px solid ${active ? C.text : C.border}`,
        background: active ? C.text : "#fff",
        color: active ? "#fff" : C.textSub,
        fontSize: 12,
        fontWeight: 600,
        cursor: "pointer",
        fontFamily: "inherit",
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </button>
  );
}

const thStyle = {
  textAlign: "left",
  padding: "9px 12px",
  fontSize: 10,
  fontWeight: 700,
  color: C.textSub,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  whiteSpace: "nowrap",
};

const tdStyle = { padding: "10px 12px", fontSize: 12, color: C.text };

export default function LabRequisitionPrint() {
  const { userInfo } = usePermissions();
  const brandingData = useContext(BrandingContext);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [patients, setPatients] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState(null);

  const [selectedKey, setSelectedKey] = useState(null);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientLoading, setPatientLoading] = useState(false);

  const [department, setDepartment] = useState(ALL_DEPARTMENTS);
  const [checkedOrderNos, setCheckedOrderNos] = useState(() => new Set());

  const listSeqRef = useRef(0);
  const patientSeqRef = useRef(0);

  useEffect(() => {
    const handle = window.setTimeout(
      () => setDebouncedSearch(search.trim()),
      SEARCH_DEBOUNCE_MS,
    );
    return () => window.clearTimeout(handle);
  }, [search]);

  // The patient picker. Search is served by the API so the page works past the
  // first page of results, exactly as the Tests & Examinations board does.
  useEffect(() => {
    let cancelled = false;
    const requestId = ++listSeqRef.current;
    setListLoading(true);
    setListError(null);
    fetchLabTestsPage(axios, getAuthHeaders(), {
      page: 1,
      pageSize: LIST_PAGE_SIZE,
      search: debouncedSearch,
    })
      .then(({ patients: rows }) => {
        if (cancelled || requestId !== listSeqRef.current) return;
        setPatients(groupPatients(rows));
      })
      .catch((err) => {
        if (cancelled || requestId !== listSeqRef.current) return;
        console.error("Could not load lab orders for requisition print:", err);
        setListError("Could not load patients. Please try again.");
        setPatients([]);
      })
      .finally(() => {
        if (!cancelled && requestId === listSeqRef.current) {
          setListLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedSearch]);

  // A search hit only carries the orders that matched it. Re-fetch the chosen
  // patient by UHID so the slip really is every test placed for them.
  const handleSelectPatient = useCallback(async (patient) => {
    setSelectedKey(patient.key);
    setSelectedPatient(patient);
    setDepartment(ALL_DEPARTMENTS);

    const uhid = dash(patient.uhid);
    const admissionId = patient.admissionId;
    if (!uhid && admissionId == null) return;

    const requestId = ++patientSeqRef.current;
    setPatientLoading(true);
    try {
      const isIpd = admissionId != null && admissionId !== "";
      const { patients: rows } = await fetchLabTestsPage(axios, getAuthHeaders(), {
        page: 1,
        pageSize: PATIENT_PAGE_SIZE,
        ...(isIpd ? { patientType: "IPD", admissionId } : { search: uhid }),
      });
      if (requestId !== patientSeqRef.current) return;
      const full =
        groupPatients(rows).find(
          (p) =>
            p.key === patient.key ||
            (patient.admissionId != null &&
              String(p.admissionId) === String(patient.admissionId)),
        ) || patient;

      // The search above is the only query that returns every test this
      // patient has, whichever order they were raised on; the detail query
      // below is the only one that reliably returns who the patient is.
      const detailQuery =
        !isIpd && (needsDemographics(full) || needsOrderDetail(full))
          ? detailQueryFor(full)
          : null;
      if (!detailQuery) {
        setSelectedPatient(full);
        return;
      }
      const { patients: detailRows } = await fetchLabTestsPage(
        axios,
        getAuthHeaders(),
        { page: 1, pageSize: PATIENT_PAGE_SIZE, ...detailQuery },
      );
      if (requestId !== patientSeqRef.current) return;
      // Only ever take demographics off a row that is the same patient — a
      // backend that ignored the filter must not put someone else's phone
      // number on this slip.
      const merged = { ...full };
      const samePatient = groupPatients(detailRows).filter(
        (row) => !uhid || dash(row.uhid) === uhid,
      );
      samePatient.forEach((row) => mergePatientDemographics(merged, row));
      // This response is the only one carrying department and sample for a
      // patient found by search, so take its order lines too rather than only
      // its demographics — dedupeOrdersByOrderNo keeps whichever copy of each
      // order says more, so the leaner search rows never overwrite it, and
      // tests the detail query does not return are still kept.
      merged.orders = dedupeOrdersByOrderNo([
        ...(full.orders || []),
        ...samePatient.flatMap((row) => row.orders || []),
      ]);
      setSelectedPatient(merged);
    } catch (err) {
      // The row we already have is a valid, if possibly partial, slip —
      // keep it rather than emptying the panel.
      console.error("Could not load the patient's full order list:", err);
    } finally {
      if (requestId === patientSeqRef.current) setPatientLoading(false);
    }
  }, []);

  const allOrders = useMemo(() => selectedPatient?.orders || [], [
    selectedPatient,
  ]);

  const departments = useMemo(() => departmentCounts(allOrders), [allOrders]);

  const visibleOrders = useMemo(() => {
    if (department === ALL_DEPARTMENTS) return allOrders;
    return allOrders.filter((o) => resolveLabDepartment(o) === department);
  }, [allOrders, department]);

  // Everything on show starts ticked — the common case is printing the whole
  // department, and unticking the odd test is quicker than ticking five.
  useEffect(() => {
    setCheckedOrderNos(new Set(visibleOrders.map((o) => o.orderNo)));
  }, [visibleOrders]);

  const selectedOrders = useMemo(
    () => visibleOrders.filter((o) => checkedOrderNos.has(o.orderNo)),
    [visibleOrders, checkedOrderNos],
  );

  const allChecked =
    visibleOrders.length > 0 && selectedOrders.length === visibleOrders.length;

  const toggleOrder = useCallback((orderNo) => {
    setCheckedOrderNos((prev) => {
      const next = new Set(prev);
      if (next.has(orderNo)) next.delete(orderNo);
      else next.add(orderNo);
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    setCheckedOrderNos(
      allChecked ? new Set() : new Set(visibleOrders.map((o) => o.orderNo)),
    );
  }, [allChecked, visibleOrders]);

  const handlePrint = useCallback(() => {
    printLabOrderRequisition({
      patient: selectedPatient,
      orders: selectedOrders,
      department: department === ALL_DEPARTMENTS ? "" : department,
      brandingData,
      userInfo,
    });
  }, [selectedPatient, selectedOrders, department, brandingData, userInfo]);

  return (
    <LabLayout>
      <div style={{ padding: "28px 32px" }}>
        <PageHeader
          title="Lab Order Requisition Print"
          subtitle="Search and select a patient, choose a department, then print all tests placed for that patient in that department."
        />

        <div
          style={{
            display: "flex",
            gap: 20,
            alignItems: "flex-start",
            flexWrap: "wrap",
          }}
        >
          {/* Patient picker */}
          <div style={{ width: 340, flexShrink: 0, minWidth: 280 }}>
            <div style={{ position: "relative", marginBottom: 14 }}>
              <span
                style={{
                  position: "absolute",
                  left: 11,
                  top: "50%",
                  transform: "translateY(-50%)",
                  display: "flex",
                }}
              >
                <SvgIcon name="search" size={15} color={C.textSub} />
              </span>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search patient name, UHID or order no."
                style={{ ...inputStyle, paddingLeft: 34 }}
              />
            </div>

            <div style={{ ...cardStyle, overflow: "hidden", minHeight: 380 }}>
              {listLoading && (
                <div
                  style={{ padding: "16px 14px", fontSize: 12, color: C.textSub }}
                >
                  Loading patients…
                </div>
              )}
              {!listLoading && listError && (
                <div
                  style={{ padding: "16px 14px", fontSize: 12, color: C.danger }}
                >
                  {listError}
                </div>
              )}
              {!listLoading && !listError && patients.length === 0 && (
                <div
                  style={{ padding: "16px 14px", fontSize: 12, color: C.textSub }}
                >
                  No patients with lab orders match this search.
                </div>
              )}
              {!listLoading &&
                !listError &&
                patients.map((p) => (
                  <PatientListRow
                    key={p.key}
                    patient={p}
                    active={p.key === selectedKey}
                    onSelect={handleSelectPatient}
                  />
                ))}
            </div>
          </div>

          {/* Requisition builder */}
          <div style={{ flex: 1, minWidth: 520 }}>
            {!selectedPatient ? (
              <div
                style={{
                  ...cardStyle,
                  padding: "48px 24px",
                  textAlign: "center",
                  fontSize: 13,
                  color: C.textSub,
                }}
              >
                Select a patient to build their requisition.
              </div>
            ) : (
              <>
                <div
                  style={{ ...cardStyle, padding: "16px 20px", marginBottom: 18 }}
                >
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: C.textSub,
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      marginBottom: 12,
                    }}
                  >
                    Patient Information
                  </div>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(180px, 1fr))",
                      rowGap: 14,
                      columnGap: 20,
                    }}
                  >
                    <InfoField
                      label="Patient Name"
                      value={selectedPatient.name}
                    />
                    <InfoField label="UHID" value={dash(selectedPatient.uhid)} />
                    <InfoField
                      label="Age / Gender"
                      value={formatAgeGender(selectedPatient)}
                    />
                    <InfoField
                      label="Encounter"
                      value={dash(selectedPatient.patientType)}
                    />
                    <InfoField
                      label="Location"
                      value={formatLocation(selectedPatient)}
                    />
                    <InfoField
                      label="Total Tests"
                      value={patientLoading ? "…" : String(allOrders.length)}
                    />
                  </div>
                </div>

                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: C.textSub,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    marginBottom: 8,
                  }}
                >
                  Department
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: 8,
                    flexWrap: "wrap",
                    marginBottom: 16,
                  }}
                >
                  <DepartmentChip
                    label={`All departments (${allOrders.length})`}
                    active={department === ALL_DEPARTMENTS}
                    onClick={() => setDepartment(ALL_DEPARTMENTS)}
                  />
                  {departments.map((d) => (
                    <DepartmentChip
                      key={d.name}
                      label={`${d.name} (${d.count})`}
                      active={department === d.name}
                      onClick={() => setDepartment(d.name)}
                    />
                  ))}
                </div>

                <div style={{ ...cardStyle, overflow: "hidden" }}>
                  <div style={{ overflowX: "auto" }}>
                    <table
                      style={{
                        width: "100%",
                        borderCollapse: "collapse",
                        minWidth: 760,
                      }}
                    >
                      <thead>
                        <tr
                          style={{
                            background: "#F7F9FB",
                            borderBottom: `1px solid ${C.border}`,
                          }}
                        >
                          <th style={{ ...thStyle, width: 42 }} />
                          <th style={thStyle}>Test / Service</th>
                          <th style={thStyle}>Department</th>
                          <th style={thStyle}>Sample</th>
                          <th style={thStyle}>Priority</th>
                          <th style={thStyle}>Order No</th>
                          <th style={thStyle}>Ordered On</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visibleOrders.length === 0 && (
                          <tr>
                            <td
                              colSpan={7}
                              style={{
                                ...tdStyle,
                                color: C.textSub,
                                textAlign: "center",
                                padding: "24px 12px",
                              }}
                            >
                              {patientLoading
                                ? "Loading this patient's tests…"
                                : "No tests for this patient in this department."}
                            </td>
                          </tr>
                        )}
                        {visibleOrders.map((o) => (
                          <tr
                            key={o.orderNo}
                            style={{ borderBottom: `1px solid ${C.border}` }}
                          >
                            <td style={{ ...tdStyle, textAlign: "center" }}>
                              <input
                                type="checkbox"
                                checked={checkedOrderNos.has(o.orderNo)}
                                onChange={() => toggleOrder(o.orderNo)}
                                aria-label={`Include ${o.service}`}
                                style={{
                                  width: 15,
                                  height: 15,
                                  cursor: "pointer",
                                  accentColor: C.primary,
                                }}
                              />
                            </td>
                            <td style={{ ...tdStyle, fontWeight: 700 }}>
                              {o.service}
                            </td>
                            <td style={{ ...tdStyle, color: C.textSub }}>
                              {resolveLabDepartment(o)}
                            </td>
                            <td style={{ ...tdStyle, color: C.textSub }}>
                              {o.sample}
                            </td>
                            <td style={tdStyle}>
                              <PriorityPill priority={o.priority} />
                            </td>
                            <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>
                              {o.orderNo}
                            </td>
                            <td
                              style={{
                                ...tdStyle,
                                color: C.textSub,
                                whiteSpace: "nowrap",
                              }}
                            >
                              {formatDateTime(o.ordered)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 10,
                    marginTop: 16,
                  }}
                >
                  <button
                    type="button"
                    onClick={toggleAll}
                    disabled={visibleOrders.length === 0}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 7,
                      padding: "9px 16px",
                      borderRadius: 8,
                      border: `1.5px solid ${C.border}`,
                      background: "#fff",
                      color: C.text,
                      fontSize: 13,
                      fontWeight: 600,
                      fontFamily: "inherit",
                      cursor:
                        visibleOrders.length === 0 ? "not-allowed" : "pointer",
                      opacity: visibleOrders.length === 0 ? 0.6 : 1,
                    }}
                  >
                    <SvgIcon name="check" size={15} color={C.text} />
                    {allChecked ? "Clear All" : "Select All"}
                  </button>
                  <button
                    type="button"
                    onClick={handlePrint}
                    disabled={selectedOrders.length === 0}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 7,
                      padding: "9px 18px",
                      borderRadius: 8,
                      border: "none",
                      background: C.text,
                      color: "#fff",
                      fontSize: 13,
                      fontWeight: 700,
                      fontFamily: "inherit",
                      cursor:
                        selectedOrders.length === 0 ? "not-allowed" : "pointer",
                      opacity: selectedOrders.length === 0 ? 0.55 : 1,
                    }}
                  >
                    <SvgIcon name="printer" size={15} color="#fff" />
                    Print Requisition ({selectedOrders.length})
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </LabLayout>
  );
}
